import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminGameListResponse, AdminGameSummary, StandaloneGameType, fetchStandaloneGames } from '../api/standaloneGames';
import './standalone-games.css';

const GAME_META: Record<StandaloneGameType, { label: string; icon: string; className: string }> = {
    flipCardGame: { label: 'Bandido', icon: 'B', className: 'bandido' },
    quizMarathon: { label: 'Zooma', icon: 'Z', className: 'zooma' },
    doubleDown: { label: 'Rolla', icon: 'R', className: 'rolla' },
};

const initialData: AdminGameListResponse = { items: [], total: 0, page: 1, limit: 12, totalPages: 1, counts: { total: 0, active: 0, archived: 0, needsAttention: 0 } };

export default function StandaloneGames() {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [gameType, setGameType] = useState('all');
    const [status, setStatus] = useState('all');
    const [difficulty, setDifficulty] = useState('all');
    const [access, setAccess] = useState('all');
    const [health, setHealth] = useState('all');
    const [sort, setSort] = useState('updatedAt');
    const [page, setPage] = useState(1);

    useEffect(() => setPage(1), [search, gameType, status, difficulty, access, health, sort]);

    useEffect(() => {
        const timer = window.setTimeout(async () => {
            try {
                setLoading(true);
                setError(null);
                setData(await fetchStandaloneGames({ page, limit: 12, search, gameType, status, difficulty, access, health, sort, direction: sort === 'title' ? 'asc' : 'desc' }));
            } catch (loadError) {
                setError(loadError instanceof Error ? loadError.message : 'Failed to load games');
            } finally {
                setLoading(false);
            }
        }, search ? 250 : 0);
        return () => window.clearTimeout(timer);
    }, [page, search, gameType, status, difficulty, access, health, sort]);

    const renderCard = (game: AdminGameSummary) => {
        const meta = GAME_META[game.gameType];
        return (
            <article className="sg-card" key={`${game.gameType}-${game.id}`}>
                <div className="sg-card-top">
                    <div className={`sg-game-icon sg-game-icon-${meta.className}`}>{meta.icon}</div>
                    <div className="sg-badges">
                        <span className={`sg-badge sg-badge-${game.status}`}>{game.status}</span>
                        <span className={`sg-badge sg-badge-${game.health === 'healthy' ? 'healthy' : 'warning'}`}>{game.health === 'healthy' ? 'Healthy' : 'Needs attention'}</span>
                    </div>
                </div>
                <h2>{game.title}</h2>
                <div className="sg-badges" style={{ marginBottom: 10 }}><span className="sg-badge">{meta.label}</span><span className="sg-badge">{game.access}</span><span className="sg-badge">{game.difficulty}</span></div>
                <p>{game.description || 'No description supplied.'}</p>
                <div className="sg-meta">
                    <div><small>Questions</small><strong>{game.questionCount}</strong></div>
                    <div><small>Played</small><strong>{game.timesPlayed}</strong></div>
                    <div><small>Creator</small><strong>{game.creator.nickname || game.creator.name}</strong></div>
                </div>
                <Link className="sg-button" to={`/standalone-games/${game.gameType}/${game.id}`}>Open workspace</Link>
            </article>
        );
    };

    return (
        <main className="sg-page">
            <div className="sg-shell">
                <header className="sg-header">
                    <div><p className="sg-eyebrow">Content operations</p><h1 className="sg-title">Standalone Games</h1><p className="sg-subtitle">Create, review, preview, and maintain every Bandido, Zooma, and Rolla template from one place.</p></div>
                    <div className="sg-actions"><Link className="sg-button sg-button-primary" to="/standalone-games/new">+ Create game</Link></div>
                </header>
                <section className="sg-stats" aria-label="Game totals">
                    <div className="sg-stat"><span>Total games</span><strong>{data.counts.total}</strong></div>
                    <div className="sg-stat"><span>Active</span><strong>{data.counts.active}</strong></div>
                    <div className="sg-stat"><span>Archived</span><strong>{data.counts.archived}</strong></div>
                    <div className="sg-stat"><span>Needs attention</span><strong>{data.counts.needsAttention}</strong></div>
                </section>
                <section className="sg-toolbar" aria-label="Game filters">
                    <input className="sg-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search title or description…" aria-label="Search games" />
                    <select className="sg-select" value={gameType} onChange={(event) => setGameType(event.target.value)}><option value="all">All games</option><option value="flipCardGame">Bandido</option><option value="quizMarathon">Zooma</option><option value="doubleDown">Rolla</option></select>
                    <select className="sg-select" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">Any status</option><option value="active">Active</option><option value="archived">Archived</option></select>
                    <select className="sg-select" value={difficulty} onChange={(event) => setDifficulty(event.target.value)}><option value="all">Any difficulty</option><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option></select>
                    <select className="sg-select" value={access} onChange={(event) => setAccess(event.target.value)}><option value="all">Any access</option><option value="public">Public</option><option value="private">Private</option></select>
                    <select className="sg-select" value={health} onChange={(event) => setHealth(event.target.value)}><option value="all">Any health</option><option value="healthy">Healthy</option><option value="needsAttention">Needs attention</option></select>
                    <select className="sg-select" value={sort} onChange={(event) => setSort(event.target.value)}><option value="updatedAt">Recently updated</option><option value="createdAt">Recently created</option><option value="timesPlayed">Most played</option><option value="title">Title A–Z</option></select>
                </section>
                {error && <div className="sg-alert sg-alert-error">{error}</div>}
                {loading ? <div className="sg-state">Loading standalone games…</div> : data.items.length ? <section className="sg-grid">{data.items.map(renderCard)}</section> : <div className="sg-state"><strong>No games match these filters.</strong><br />Try clearing a filter or create a new game.</div>}
                <div className="sg-pagination"><button className="sg-button sg-button-small" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</button><span>Page {data.page} of {data.totalPages}</span><button className="sg-button sg-button-small" disabled={page >= data.totalPages || loading} onClick={() => setPage((value) => value + 1)}>Next</button></div>
            </div>
        </main>
    );
}
